// Spec 48 ai-ux — streaming_cursor.
//
// "The blinking indicator at the current token position during assistant
// streaming. Respects reduced-motion: becomes a static pipe. Disappears
// on stream completion." (spec 48 line 647)
//
// a11y: aria-hidden — decorative; screen readers receive content updates
// via the aria-live region on the bubble content (managed by ChatBubble.
// Assistant variant), not via the cursor itself.

import { forwardRef, type HTMLAttributes } from "react";

import { cx } from "../lib/cx.ts";

export interface StreamingCursorProps extends HTMLAttributes<HTMLSpanElement> {
  /**
   * When true, render the static-pipe variant (skips the blink animation).
   * Caller resolves prefers-reduced-motion via media query and passes
   * the boolean down — keeps this component pure + SSR-safe.
   */
  readonly reducedMotion?: boolean;
}

export const StreamingCursor = forwardRef<HTMLSpanElement, StreamingCursorProps>(
  ({ reducedMotion = false, className, ...rest }, ref) => (
    <span
      ref={ref}
      aria-hidden="true"
      data-component="streaming-cursor"
      data-reduced-motion={reducedMotion || undefined}
      className={cx(
        "nai-streaming-cursor",
        reducedMotion ? "nai-streaming-cursor--static" : "nai-streaming-cursor--blink",
        className,
      )}
      {...rest}
    >
      {/* Static pipe under reduced motion; CSS animates opacity 0.4 ↔ 1.0
          via the .nai-streaming-cursor--blink keyframe in the consumer's
          stylesheet. */}
      |
    </span>
  ),
);
StreamingCursor.displayName = "StreamingCursor";
